// Remotion 렌더와 에디터 모두에서 공유할 로컬 폰트 메타데이터 정의

import type { FontFamilyValue } from '@crabs-ai/deck-preset-contract';

// 지원하는 font weight 타입
export type FontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

// Static font의 weight별 파일 경로
type FontSource = {
  path: string;
  weight: FontWeight;
};

// Variable Font 정의 - 하나의 파일로 모든 weight 지원
type VariableFontDefinition = {
  family: string;
  displayName?: string;
  type: 'variable';
  path: string;
  supportedWeights: readonly FontWeight[];
  defaultWeight?: FontWeight; // 기본 weight (프리뷰 등에서 사용)
};

// Static Font 정의 - weight별 개별 파일
type StaticFontDefinition = {
  family: string;
  displayName?: string;
  type: 'static';
  sources: readonly FontSource[];
  defaultWeight?: FontWeight; // 기본 weight (프리뷰 등에서 사용)
};

// 통합 폰트 정의 타입
export type FontDefinition = VariableFontDefinition | StaticFontDefinition;

// 타입 가드
export const isVariableFont = (
  font: FontDefinition,
): font is VariableFontDefinition => font.type === 'variable';

export const isStaticFont = (
  font: FontDefinition,
): font is StaticFontDefinition => font.type === 'static';

// 헬퍼 함수로 타입 안전하게 정의
const defineFonts = <const T extends readonly FontDefinition[]>(fonts: T) =>
  fonts;

export const FONTS_TO_LOAD = defineFonts([
  // ============================================
  // Variable Fonts - 하나의 파일로 모든 weight 지원
  // ============================================
  {
    family: 'Pretendard',
    type: 'variable',
    path: 'fonts/pretendard-variable.woff2',
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    defaultWeight: 400,
  },
  {
    family: 'Freesentation',
    type: 'variable',
    path: 'fonts/FreesentationVF.woff2',
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Square Neo',
    type: 'variable',
    path: 'fonts/NanumSquareNeo-Variable.woff',
    supportedWeights: [300, 400, 700, 800, 900],
    defaultWeight: 400,
  },
  {
    family: 'SUIT',
    type: 'variable',
    path: 'fonts/SUIT-Variable.woff2',
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    defaultWeight: 400,
  },
  {
    family: 'Fredoka',
    type: 'variable',
    path: 'fonts/Fredoka-VariableFont_wdth,wght.ttf',
    supportedWeights: [300, 400, 500, 600, 700],
    defaultWeight: 400,
  },

  // ============================================
  // Static Fonts
  // ============================================
  {
    family: 'Noto Sans KR',
    type: 'static',
    sources: [
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-100.woff2',
        weight: 100,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-200.woff2',
        weight: 200,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-300.woff2',
        weight: 300,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-400.woff2',
        weight: 400,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-500.woff2',
        weight: 500,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-600.woff2',
        weight: 600,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-700.woff2',
        weight: 700,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-800.woff2',
        weight: 800,
      },
      {
        path: 'fonts/noto-sans-kr-v38-korean_latin-900.woff2',
        weight: 900,
      },
    ],
    defaultWeight: 400,
  },
  {
    family: 'LINE Seed Sans KR',
    type: 'static',
    sources: [
      { path: 'fonts/LINESeedKR-Th.woff2', weight: 100 },
      { path: 'fonts/LINESeedKR-Rg.woff2', weight: 400 },
      { path: 'fonts/LINESeedKR-Bd.woff2', weight: 700 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Gothic A1',
    type: 'static',
    sources: [{ path: 'fonts/gothic-a1.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Hahmlet',
    type: 'static',
    sources: [{ path: 'fonts/hahmlet.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'IBM Plex Sans KR',
    type: 'static',
    sources: [{ path: 'fonts/ibm-plex-sans-kr.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Noto Serif KR',
    type: 'static',
    sources: [{ path: 'fonts/noto-serif-kr.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Dongle',
    type: 'static',
    sources: [{ path: 'fonts/dongle.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Gowun Batang',
    type: 'static',
    sources: [{ path: 'fonts/gowun-batang.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Gothic',
    type: 'static',
    sources: [{ path: 'fonts/nanum-gothic.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Myeongjo',
    type: 'static',
    sources: [{ path: 'fonts/nanum-myeongjo.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Jua',
    type: 'static',
    sources: [{ path: 'fonts/Jua-Regular.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Asta Sans',
    type: 'static',
    sources: [{ path: 'fonts/asta-sans.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Bagel Fat One',
    type: 'static',
    sources: [{ path: 'fonts/bagel-fat-one.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Black Han Sans',
    type: 'static',
    sources: [{ path: 'fonts/black-han-sans.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Diphylleia',
    type: 'static',
    sources: [{ path: 'fonts/diphylleia.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Do Hyeon',
    type: 'static',
    sources: [{ path: 'fonts/do-hyeon.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Dokdo',
    type: 'static',
    sources: [{ path: 'fonts/dokdo.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'East Sea Dokdo',
    type: 'static',
    sources: [{ path: 'fonts/east-sea-dokdo.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Gasoek One',
    type: 'static',
    sources: [{ path: 'fonts/gasoek-one.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Gowun Dodum',
    type: 'static',
    sources: [{ path: 'fonts/gowun-dodum.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Grandiflora One',
    type: 'static',
    sources: [{ path: 'fonts/grandiflora-one.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Gugi',
    type: 'static',
    sources: [{ path: 'fonts/gugi.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Kirang Haerang',
    type: 'static',
    sources: [{ path: 'fonts/kirang-haerang.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Brush Script',
    type: 'static',
    sources: [{ path: 'fonts/nanum-brush-script.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Pen Script',
    type: 'static',
    sources: [{ path: 'fonts/nanum-pen-script.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Orbit',
    type: 'static',
    sources: [{ path: 'fonts/orbit.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Poor Story',
    type: 'static',
    sources: [{ path: 'fonts/poor-story.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Song Myung',
    type: 'static',
    sources: [{ path: 'fonts/song-myung.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Euljiro10YearsLater',
    displayName: 'Baemin Euljiro 10 Years',
    type: 'static',
    sources: [{ path: 'fonts/BM_EULJIRO_10yrs.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Baemin Kkubulim',
    type: 'static',
    sources: [{ path: 'fonts/BM_Kkubulim.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Baemin Yeonsung',
    type: 'static',
    sources: [{ path: 'fonts/BM_YEONSUNG.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Bookk Gothic',
    type: 'static',
    sources: [{ path: 'fonts/BookkGothic_Light.woff', weight: 300 }],
    defaultWeight: 300,
  },
  {
    family: 'Bookk Myungjo',
    type: 'static',
    sources: [{ path: 'fonts/BookkMyungjo_Light.woff', weight: 300 }],
    defaultWeight: 300,
  },
  {
    family: 'Cookie Run Black',
    type: 'static',
    sources: [{ path: 'fonts/CookieRun_Black.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cookie Run Bold',
    type: 'static',
    sources: [{ path: 'fonts/CookieRun_Bold.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cookie Run Regular',
    type: 'static',
    sources: [{ path: 'fonts/CookieRun_Regular.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Recipe Korea',
    type: 'static',
    sources: [{ path: 'fonts/Recipekorea.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Anemone',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Anemone.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 AnemoneAir',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24AnemoneAir.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Classic Type',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24ClassicType.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Dangdanghae',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Dangdanghae.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Dangeonghae',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Danjeonghae.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Dongdong',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Dongdong.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Gowoonbam',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Gowoonbam.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 MoyaMoya',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24MoyaMoya.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 MoyaMoyaFace',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24MoyaMoyaFace.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 ProSlim',
    type: 'static',
    sources: [
      { path: 'fonts/Cafe24ProSlim_300.woff', weight: 300 },
      { path: 'fonts/Cafe24ProSlim_400.woff', weight: 400 },
      { path: 'fonts/Cafe24ProSlim_700.woff', weight: 700 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 ProUp',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24ProUp.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 ShiningStar',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24ShiningStar.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Simple',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Simple.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 SsongSsong',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24SsongSsong.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Ssukssuk',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Ssukssuk.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 SuperMagic',
    type: 'static',
    sources: [
      {
        path: 'fonts/Cafe24SuperMagic_Regular.woff',
        weight: 400,
      },
      { path: 'fonts/Cafe24SuperMagic_Bold.woff', weight: 700 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 Surround',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24Surround.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Cafe24 SurroundAir',
    type: 'static',
    sources: [{ path: 'fonts/Cafe24SurroundAir.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'ClipArt Korea',
    type: 'static',
    sources: [
      { path: 'fonts/ClipArtKorea_Light.woff2', weight: 300 },
      {
        path: 'fonts/ClipArtKorea_Regular.woff2',
        weight: 400,
      },
      { path: 'fonts/ClipArtKorea_Medium.woff2', weight: 500 },
      { path: 'fonts/ClipArtKorea_Bold.woff2', weight: 700 },
    ],
    defaultWeight: 500,
  },
  {
    family: 'Gana Chocolate',
    type: 'static',
    sources: [{ path: 'fonts/GanaChocolate.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Gmarket Sans',
    type: 'static',
    sources: [
      { path: 'fonts/GmarketSansLight.woff', weight: 300 },
      { path: 'fonts/GmarketSansMedium.woff', weight: 500 },
      { path: 'fonts/GmarketSansBold.woff', weight: 700 },
    ],
    defaultWeight: 500,
  },
  {
    family: 'HS Jandari',
    type: 'static',
    sources: [{ path: 'fonts/HSJandari-Regular.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Square Round',
    type: 'static',
    sources: [
      { path: 'fonts/NanumSquareRound_Light.woff', weight: 300 },
      { path: 'fonts/NanumSquareRound_Regular.woff', weight: 400 },
      { path: 'fonts/NanumSquareRound_Bold.woff', weight: 700 },
      { path: 'fonts/NanumSquareRound_ExtraBold.woff', weight: 800 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Nanum Square',
    type: 'static',
    sources: [
      { path: 'fonts/NanumSquare_Light.woff', weight: 300 },
      { path: 'fonts/NanumSquare_Regular.woff', weight: 400 },
      { path: 'fonts/NanumSquare_Bold.woff', weight: 700 },
      { path: 'fonts/NanumSquare_ExraBold.woff', weight: 800 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'One Store Mobile Gothic Body',
    type: 'static',
    sources: [
      { path: 'fonts/OneStoreMobileGothicBody_Light.woff2', weight: 300 },
      { path: 'fonts/OneStoreMobileGothicBody_Regular.woff2', weight: 400 },
      { path: 'fonts/OneStoreMobileGothicBody_Bold.woff2', weight: 700 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'One Store Mobile Gothic Title',
    type: 'static',
    sources: [{ path: 'fonts/OneStoreMobileGothicTitle.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'One Store Mobile Pop',
    type: 'static',
    sources: [{ path: 'fonts/OneStoreMobilePop.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Ongleip Sea Breeze',
    type: 'static',
    sources: [{ path: 'fonts/OngleIpSeaBreeze.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Ongleip Eoyeonce',
    type: 'static',
    sources: [{ path: 'fonts/OngleipEoyeonce.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Paperlogy',
    type: 'static',
    sources: [
      { path: 'fonts/Paperlogy-1Thin.woff', weight: 100 },
      { path: 'fonts/Paperlogy-2ExtraLight.woff', weight: 200 },
      { path: 'fonts/Paperlogy-3Light.woff', weight: 300 },
      { path: 'fonts/Paperlogy-4Regular.woff', weight: 400 },
      { path: 'fonts/Paperlogy-5Medium.woff', weight: 500 },
      { path: 'fonts/Paperlogy-6SemiBold.woff', weight: 600 },
      { path: 'fonts/Paperlogy-7Bold.woff', weight: 700 },
      { path: 'fonts/Paperlogy-8ExtraBold.woff', weight: 800 },
      { path: 'fonts/Paperlogy-9Black.woff', weight: 900 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Rix Inoo Ari Duri',
    type: 'static',
    sources: [{ path: 'fonts/RixInooAriDuri_Regular.woff2', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Sandoll Nemony2',
    type: 'static',
    sources: [{ path: 'fonts/SDNemony2jObliqueHv.otf', weight: 900 }],
    defaultWeight: 900,
  },
  {
    family: 'SDHoyoyo',
    type: 'static',
    sources: [
      { path: 'fonts/SDHoyoyo2-aTh.otf', weight: 100 },
      { path: 'fonts/SDHoyoyo2-bLt.otf', weight: 300 },
      { path: 'fonts/SDHoyoyo2-cRg.otf', weight: 400 },
      { path: 'fonts/SDHoyoyo2-dBd.otf', weight: 700 },
      { path: 'fonts/SDHoyoyo2-eHv.otf', weight: 900 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'School Safety Notification',
    type: 'static',
    sources: [
      { path: 'fonts/SchoolSafetyNotification_Regular.woff2', weight: 400 },
      { path: 'fonts/SchoolSafetyNotification_Bold.woff2', weight: 700 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Seoul Hangang',
    type: 'static',
    sources: [
      { path: 'fonts/SeoulHangang_Light.woff2', weight: 300 },
      { path: 'fonts/SeoulHangang_Medium.woff2', weight: 500 },
      { path: 'fonts/SeoulHangang_Bold.woff2', weight: 700 },
      { path: 'fonts/SeoulHangang_ExtraBold.woff2', weight: 800 },
    ],
    defaultWeight: 500,
  },
  {
    family: 'Seoul Namsan',
    type: 'static',
    sources: [
      { path: 'fonts/SeoulNamsan_Light.woff2', weight: 300 },
      { path: 'fonts/SeoulNamsan_Medium.woff2', weight: 500 },
      { path: 'fonts/SeoulNamsan_Bold.woff2', weight: 700 },
      { path: 'fonts/SeoulNamsan_ExraBold.woff2', weight: 800 },
    ],
    defaultWeight: 500,
  },
  {
    family: 'Seoul Notice',
    type: 'static',
    sources: [
      { path: 'fonts/SeoulNotice_Medium.woff2', weight: 500 },
      { path: 'fonts/SeoulNotice_Bold.woff2', weight: 700 },
      { path: 'fonts/SeoulNotice_ExtraBold.woff2', weight: 800 },
      { path: 'fonts/SeoulNotice_Heavy.woff2', weight: 900 },
    ],
    defaultWeight: 500,
  },
  {
    family: 'The Jamsil',
    type: 'static',
    sources: [
      { path: 'fonts/TheJamsil_1Thin.woff2', weight: 100 },
      { path: 'fonts/TheJamsil_2Light.woff2', weight: 200 },
      { path: 'fonts/TheJamsil_3Regular.woff2', weight: 400 },
      { path: 'fonts/TheJamsil_4Medium.woff2', weight: 500 },
      { path: 'fonts/TheJamsil_5Bold.woff2', weight: 700 },
      { path: 'fonts/TheJamsil_6ExtraBold.woff2', weight: 800 },
    ],
    defaultWeight: 400,
  },
  {
    family: 'Ttangs Budae Jjigae',
    type: 'static',
    sources: [
      { path: 'fonts/TtangsBudaeJjigae_Light.woff2', weight: 300 },
      { path: 'fonts/TtangsBudaeJjigae_Medium.woff2', weight: 500 },
      { path: 'fonts/TtangsBudaeJjigae_Bold.woff2', weight: 700 },
    ],
    defaultWeight: 500,
  },
  {
    family: 'Yeogi Ottae Jalnan',
    type: 'static',
    sources: [{ path: 'fonts/YeogiOttaeJalnan.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Yeogi Ottae Jalnan Gothic',
    type: 'static',
    sources: [{ path: 'fonts/YeogiOttaeJalnanGothic.woff', weight: 400 }],
    defaultWeight: 400,
  },
  {
    family: 'Neurimbo Gothic',
    type: 'static',
    sources: [{ path: 'fonts/neurimboGothicRegular.woff2', weight: 400 }],
    defaultWeight: 400,
  },
] as const);

// 폰트 패밀리 값 타입 — @crabs-ai/deck-preset-contract(web·admin 공용 이름 목록)가 단일 소스.
// 이 파일(실제 폰트 파일 로더)에 이름을 새로 정의하지 않는다 — 드리프트 방지.
export type { FontFamilyValue };

// FONTS_TO_LOAD 의 실제 family 리터럴 유니온(파생, 변경 없음) — 아래 정합성 검사에만 쓰인다.
type LoadedFontFamily = (typeof FONTS_TO_LOAD)[number]['family'];

// 컴파일 타임 정합성 강제: 패키지 이름 목록(FontFamilyValue)과 FONTS_TO_LOAD 의 family 집합이
// 어긋나면(누락·초과 어느 쪽이든) 아래 대입이 `never` 슬롯에 `true` 를 넣게 돼 컴파일 에러가 난다.
type MissingFromFontsToLoad = Exclude<FontFamilyValue, LoadedFontFamily>;
type ExtraInFontsToLoad = Exclude<LoadedFontFamily, FontFamilyValue>;
const _assertFontNamesMatchPackage: [
  MissingFromFontsToLoad extends never ? true : never,
  ExtraInFontsToLoad extends never ? true : never,
] = [true, true];
void _assertFontNamesMatchPackage;

// ============================================
// 유틸리티 함수들
// ============================================

/**
 * 폰트의 지원 weight 목록을 반환
 */
export const getSupportedWeights = (font: FontDefinition): FontWeight[] => {
  if (isVariableFont(font)) {
    return [...font.supportedWeights];
  }
  return font.sources.map((s) => s.weight);
};

/**
 * family 이름으로 폰트 정의를 찾음
 */
export const getFontByFamily = (family: string): FontDefinition | undefined => {
  return FONTS_TO_LOAD.find((f) => f.family === family);
};

/**
 * 폰트 로드용 배열 생성 (Remotion loadFont에서 사용)
 * Variable font는 한 번만, Static font는 각 weight별로 엔트리 생성
 */
export const getFontLoadEntries = () => {
  const entries: { family: string; path: string; weight: string }[] = [];

  for (const font of FONTS_TO_LOAD) {
    if (font.type === 'variable') {
      // Variable font는 첫 번째 weight로 한 번만 로드
      entries.push({
        family: font.family,
        path: font.path,
        weight: '400', // variable font는 weight 속성이 크게 의미 없음
      });
    } else {
      // Static font는 각 source별로 로드
      for (const source of font.sources) {
        entries.push({
          family: font.family,
          path: source.path,
          weight: String(source.weight),
        });
      }
    }
  }

  return entries;
};

// ============================================
// 기존 호환성을 위한 파생 데이터
// ============================================

/**
 * Weight별 path 정보를 담은 타입
 */
export type FontSourceInfo = {
  defaultPath: string; // 기본 path
  defaultWeight: FontWeight; // 기본 weight (프리뷰 등에서 사용)
  pathByWeight: Map<FontWeight, string>; // weight별 path 맵
  isVariable: boolean; // variable font 여부
};

/**
 * 폰트의 기본 weight 계산
 */
const calculateDefaultWeight = (font: FontDefinition): FontWeight => {
  // 폰트 정의에 defaultWeight가 명시되어 있으면 사용
  if ('defaultWeight' in font && font.defaultWeight) {
    return font.defaultWeight;
  }

  if (font.type === 'variable') {
    // Variable font: 400이 있으면 400, 없으면 첫 번째
    return font.supportedWeights.includes(400)
      ? 400
      : (font.supportedWeights[0] ?? 400);
  } else {
    // Static font: 400이 있으면 400, 없으면 첫 번째 source의 weight
    const weights = font.sources.map((s) => s.weight);
    return weights.includes(400) ? 400 : (font.sources[0]?.weight ?? 400);
  }
};

/**
 * 폰트 소스 맵 (weight별 path 조회용)
 */
export const FONT_SOURCE_MAP = new Map<string, FontSourceInfo>(
  FONTS_TO_LOAD.map((font) => {
    const defaultWeight = calculateDefaultWeight(font);

    if (font.type === 'variable') {
      // Variable font는 모든 weight에 같은 path 사용
      const pathByWeight = new Map<FontWeight, string>();
      for (const weight of font.supportedWeights) {
        pathByWeight.set(weight, font.path);
      }
      return [
        font.family,
        {
          defaultPath: font.path,
          defaultWeight,
          pathByWeight,
          isVariable: true,
        },
      ];
    } else {
      // Static font는 weight별로 다른 path
      const pathByWeight = new Map<FontWeight, string>();
      for (const source of font.sources) {
        pathByWeight.set(source.weight, source.path);
      }
      const defaultPath = pathByWeight.get(defaultWeight)!;
      return [
        font.family,
        {
          defaultPath,
          defaultWeight,
          pathByWeight,
          isVariable: false,
        },
      ];
    }
  }),
);

/**
 * 폰트의 기본 weight를 반환
 * @param family 폰트 패밀리 이름
 * @returns 기본 weight
 */
export const getFontDefaultWeight = (family: string): FontWeight => {
  const sourceInfo = FONT_SOURCE_MAP.get(family);
  return sourceInfo?.defaultWeight ?? 400;
};

/**
 * 폰트 family와 weight로 올바른 path를 반환
 * @param family 폰트 패밀리 이름
 * @param weight 원하는 weight (생략시 기본 weight 사용)
 * @returns 파일 경로
 */
export const getFontPathByWeight = (
  family: string,
  weight?: FontWeight,
): string => {
  const sourceInfo = FONT_SOURCE_MAP.get(family);
  if (!sourceInfo) {
    console.warn(`Font source not found for ${family}`);
    return '';
  }

  // weight가 없으면 기본 weight 사용
  const targetWeight = weight ?? sourceInfo.defaultWeight;

  // Variable font는 모든 weight에 같은 path
  if (sourceInfo.isVariable) {
    return sourceInfo.defaultPath;
  }

  // Static font: 해당 weight가 있으면 반환
  if (sourceInfo.pathByWeight.has(targetWeight)) {
    return sourceInfo.pathByWeight.get(targetWeight)!;
  }

  // 가장 가까운 weight 찾기
  const availableWeights = Array.from(sourceInfo.pathByWeight.keys());
  let closestWeight = availableWeights[0]!;
  let minDiff = Math.abs(targetWeight - closestWeight);

  for (const w of availableWeights) {
    const diff = Math.abs(targetWeight - w);
    if (diff < minDiff) {
      minDiff = diff;
      closestWeight = w;
    }
  }

  return sourceInfo.pathByWeight.get(closestWeight)!;
};

/**
 * CSS 변수 매핑 - FontFamilyValue가 그대로 CSS font-family 값
 */
export const FONT_FAMILY_CSS_VARIABLES = FONTS_TO_LOAD.reduce(
  (acc, font) => {
    acc[font.family as FontFamilyValue] = font.family;
    return acc;
  },
  {} as Record<FontFamilyValue, string>,
);

/**
 * 표시용 이름 매핑
 */
export const FONT_DISPLAY_NAMES = FONTS_TO_LOAD.reduce(
  (acc, font) => {
    const displayName =
      'displayName' in font && font.displayName
        ? font.displayName
        : font.family;
    acc[font.family as FontFamilyValue] = displayName;
    return acc;
  },
  {} as Record<FontFamilyValue, string>,
);

/**
 * 폰트 선택 리스트 (UI 컴포넌트용)
 */
export const FONT_FAMILY_LIST = FONTS_TO_LOAD.map((font) => {
  const displayName =
    'displayName' in font && font.displayName ? font.displayName : font.family;
  return {
    key: font.family as FontFamilyValue,
    name: displayName,
    value: font.family,
  };
});

/**
 * 폰트별 지원 weight 매핑
 */
export const FONT_WEIGHTS_BY_FAMILY = FONTS_TO_LOAD.reduce(
  (acc, font) => {
    acc[font.family as FontFamilyValue] = getSupportedWeights(font);
    return acc;
  },
  {} as Record<FontFamilyValue, FontWeight[]>,
);

// 기존 이름 호환성
export const FONT_WEIGHTS_BY_FAMILY_FALLBACK = FONT_WEIGHTS_BY_FAMILY;

/**
 * 기본 로드 weight 반환
 */
export const getDefaultLoadWeight = () => '400';
